import React, { useState, useCallback, useEffect, useRef } from 'react';

interface DropzoneOverlayProps {
  onDrop: (files: File[]) => void;
}

const DropzoneOverlay: React.FC<DropzoneOverlayProps> = ({ onDrop }) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const timeoutRef = useRef<number | null>(null);

  const handleDrag = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragOut = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = window.setTimeout(() => setIsDragging(false), 100);
    }
  }, []);

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      onDrop(droppedFiles);
      e.dataTransfer.clearData();
    }
  }, [onDrop]);

  useEffect(() => {
    window.addEventListener('dragenter', handleDragIn);
    window.addEventListener('dragleave', handleDragOut);
    window.addEventListener('dragover', handleDrag);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragenter', handleDragIn);
      window.removeEventListener('dragleave', handleDragOut);
      window.removeEventListener('dragover', handleDrag);
      window.removeEventListener('drop', handleDrop);

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [handleDragIn, handleDragOut, handleDrag, handleDrop]);

  if (!isDragging) return null;

  return (
    <div className="bg-gray-200 bg-opacity-75 fixed inset-0 flex items-center justify-center z-50">
      <div className="text-2xl font-semibold text-black">Drop files here to upload</div>
    </div>
  );
};

export default DropzoneOverlay;
